import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import { LABEL_FONT_STACKS } from './types';
const HANDLE_SIZE = 10;
const HANDLE_HALF = HANDLE_SIZE / 2;
// Mirror LabelMapOverlay's world-space scale clamps so the editor
// textarea renders at the same on-screen size as the committed label.
const MIN_SCALE = 0.1;
const MAX_SCALE = 8;
const computeScale = (ref, dist) => {
    if (!ref || ref <= 0 || dist <= 0)
        return 1;
    const s = ref / dist;
    if (s < MIN_SCALE)
        return MIN_SCALE;
    if (s > MAX_SCALE)
        return MAX_SCALE;
    return s;
};
// Matches LabelMapOverlay.tsx — see comment there for rationale.
const STROKE_CSS = '3px rgba(0,0,0,0.95)';
const backgroundFor = (kind) => {
    if (kind === 'white')
        return 'rgba(255,255,255,0.85)';
    if (kind === 'black')
        return 'rgba(0,0,0,0.7)';
    return undefined;
};
// DOM <textarea> overlaid on the Cesium canvas at the label's screen-
// space position. Parent updates `screenPosition` every Cesium render
// frame so the editor tracks the label as the camera moves. The visible
// font matches LabelMapOverlay so what you type is what you see.
//
// Editing semantics (Figma-style):
//   Enter         → commit
//   Shift+Enter   → newline (multi-line label)
//   Escape        → cancel
//   blur          → commit
export const LabelInlineEditor = forwardRef(({ viewer, label, screenPosition, onCommit, onCancel, onTextChange, onResize, }, ref) => {
    var _a;
    const [text, setText] = useState(label.text);
    const inputRef = useRef(null);
    const committedRef = useRef(false);
    // Keep latest text accessible from the imperative commit handle even
    // if React state hasn't re-rendered yet when the parent calls.
    const textRef = useRef(text);
    useEffect(() => { textRef.current = text; }, [text]);
    // Track the same world-space scale the overlay uses for the committed
    // label so the textarea visually matches it as the camera moves.
    const [scale, setScale] = useState(1);
    // Textarea's on-screen bounding rect (including the scale transform).
    // Used to position the four resize handles around it.
    const [textareaRect, setTextareaRect] = useState(null);
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || !label.position)
            return;
        const recompute = () => {
            const dist = Cesium.Cartesian3.distance(viewer.camera.positionWC, label.position);
            const next = computeScale(label.referenceDistance, dist);
            setScale(prev => Math.abs(prev - next) > 0.005 ? next : prev);
            if (inputRef.current) {
                const r = inputRef.current.getBoundingClientRect();
                setTextareaRect(prev => {
                    if (prev
                        && Math.abs(prev.left - r.left) < 0.5
                        && Math.abs(prev.top - r.top) < 0.5
                        && Math.abs(prev.right - r.right) < 0.5
                        && Math.abs(prev.bottom - r.bottom) < 0.5) {
                        return prev;
                    }
                    return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
                });
            }
        };
        recompute();
        const remove = viewer.scene.postRender.addEventListener(recompute);
        return remove;
    }, [viewer, label.position, label.referenceDistance]);
    // Drag handler for the corner resize handles. Mirrors the math in
    // LabelMapOverlay: cursor distance from the textarea center maps to a
    // scaled-size ratio applied to label.size.
    const startResize = (e) => {
        if (!onResize)
            return;
        e.stopPropagation();
        e.preventDefault();
        const cx = screenPosition.x;
        const cy = screenPosition.y;
        const dx0 = e.clientX - cx;
        const dy0 = e.clientY - cy;
        const dist0 = Math.max(Math.sqrt(dx0 * dx0 + dy0 * dy0), 1);
        const initialSize = label.size;
        const onMove = (ev) => {
            const dxN = ev.clientX - cx;
            const dyN = ev.clientY - cy;
            const distN = Math.sqrt(dxN * dxN + dyN * dyN);
            const ratio = distN / dist0;
            const clamped = Math.max(8, Math.min(200, Math.round(initialSize * ratio)));
            onResize(label.id, clamped);
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };
    useEffect(() => {
        var _a, _b;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.select();
    }, []);
    useImperativeHandle(ref, () => ({
        commit: () => {
            if (committedRef.current)
                return;
            committedRef.current = true;
            onCommit(textRef.current);
        },
        getText: () => textRef.current,
        focus: () => { var _a; (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
    }), [onCommit]);
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            committedRef.current = true;
            onCommit(text);
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            committedRef.current = true;
            onCancel();
        }
        // Shift+Enter falls through to the textarea's default newline.
    };
    const handleBlur = (e) => {
        if (committedRef.current)
            return;
        // Focus moving into the style popout (e.g., user clicks a <select>
        // to change font/size) shouldn't commit — the popout's controls are
        // part of the same editing gesture. The textarea is re-focused
        // after the select change so further typing still goes here.
        const next = e.relatedTarget;
        if (next && next.closest('[data-label-style-popout]'))
            return;
        committedRef.current = true;
        onCommit(text);
    };
    const fontStack = (_a = LABEL_FONT_STACKS[label.fontFamily]) !== null && _a !== void 0 ? _a : LABEL_FONT_STACKS.sans;
    const bgColor = backgroundFor(label.background);
    const hasBg = !!bgColor;
    const showBgBorder = hasBg && !!label.backgroundBorder;
    const corner = label.cornerRadius;
    const radius = corner === 'square' ? 0 : corner === 'pill' ? 9999 : 8;
    // Hidden span used to measure the exact rendered text dimensions so the
    // textarea can size to match the static label precisely. Using `cols` /
    // intrinsic textarea sizing under-estimates because the stroke extends
    // beyond glyph bounds and font widths vary by character.
    const measureRef = useRef(null);
    const [measured, setMeasured] = useState({ w: 0, h: 0 });
    useEffect(() => {
        if (!measureRef.current)
            return;
        const r = measureRef.current.getBoundingClientRect();
        setMeasured(prev => (Math.abs(prev.w - r.width) > 0.5 || Math.abs(prev.h - r.height) > 0.5)
            ? { w: r.width, h: r.height }
            : prev);
    }, [text, label.size, label.fontFamily, label.bold, label.italic, label.stroke]);
    // Same font properties the static overlay uses for rendering. Letter
    // spacing matches the overlay's 0.01em so widths stay consistent.
    const sharedTextStyle = {
        fontFamily: fontStack,
        fontSize: `${label.size}px`,
        fontWeight: label.bold ? 700 : 400,
        fontStyle: label.italic ? 'italic' : 'normal',
        letterSpacing: '0.01em',
        whiteSpace: 'pre',
        WebkitTextStroke: label.stroke ? STROKE_CSS : undefined,
        paintOrder: label.stroke ? 'stroke fill' : undefined,
    };
    // Placeholder text when label is empty so the box has something to size
    // around. Single 'M' is a safe ceiling for character width.
    const measuredText = text.length > 0 ? text : 'M';
    // Padding formula mirrors LabelMapOverlay: descender room scaled by
    // font size, Pill curve clearance, and stroke-ring breathing room when
    // -webkit-text-stroke is on (otherwise the stroked cap-tops bleed
    // past the pill's top edge).
    const strokeRoom = label.stroke ? 3 : 0;
    const vBase = Math.max(2, Math.round(label.size * 0.18));
    const padY = hasBg ? (showBgBorder ? vBase - 1 : vBase) + strokeRoom : 0;
    const baseX = showBgBorder ? 5 : 6;
    const pillExtra = label.cornerRadius === 'pill' ? Math.round(label.size * 0.35) : 0;
    const padX = hasBg ? baseX + pillExtra + strokeRoom : 0;
    return (_jsxs(_Fragment, { children: [_jsx("span", { ref: measureRef, "aria-hidden": "true", style: {
                    ...sharedTextStyle,
                    position: 'fixed',
                    left: -99999,
                    top: -99999,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                }, children: measuredText }), _jsx("textarea", { ref: inputRef, value: text, onChange: (e) => {
                    setText(e.target.value);
                    onTextChange === null || onTextChange === void 0 ? void 0 : onTextChange(e.target.value);
                }, onKeyDown: handleKeyDown, onBlur: handleBlur, onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), placeholder: "Type then press Enter\u2026", className: "fixed z-[70] outline-none", style: {
                    left: screenPosition.x,
                    top: screenPosition.y,
                    transform: `translate(-50%, -50%) scale(${scale})`,
                    transformOrigin: 'center',
                    ...sharedTextStyle,
                    color: label.color,
                    background: bgColor !== null && bgColor !== void 0 ? bgColor : 'transparent',
                    // Match the final rendered label's border / radius so the user
                    // sees a consistent look while typing. Without a background, we
                    // keep the dashed editor outline as a visual placement hint.
                    border: showBgBorder
                        ? `1px solid ${label.color}`
                        : (hasBg ? 'none' : '1px dashed rgba(255,255,255,0.75)'),
                    borderRadius: hasBg ? radius : 4,
                    padding: hasBg ? `${padY}px ${padX}px` : '0',
                    // Width + height are driven by the measured text so the editor
                    // matches the static label pixel-for-pixel (including extra
                    // Pill padX and stroke breathing room).
                    width: measured.w > 0 ? `${measured.w + padX * 2}px` : 'auto',
                    height: measured.h > 0 ? `${measured.h + padY * 2}px` : 'auto',
                    boxSizing: 'border-box',
                    textAlign: 'center',
                    caretColor: label.color,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    resize: 'none',
                    overflow: 'hidden',
                } }), onResize && textareaRect && text.length > 0 && (() => {
                const corners = [
                    { x: textareaRect.left, y: textareaRect.top, cursor: 'nwse-resize' },
                    { x: textareaRect.right, y: textareaRect.top, cursor: 'nesw-resize' },
                    { x: textareaRect.left, y: textareaRect.bottom, cursor: 'nesw-resize' },
                    { x: textareaRect.right, y: textareaRect.bottom, cursor: 'nwse-resize' },
                ];
                return corners.map((c, i) => (_jsx("div", { style: {
                        position: 'fixed',
                        left: c.x - HANDLE_HALF,
                        top: c.y - HANDLE_HALF,
                        width: HANDLE_SIZE,
                        height: HANDLE_SIZE,
                        backgroundColor: '#ffffff',
                        border: '1px solid #1f2937',
                        borderRadius: 2,
                        pointerEvents: 'auto',
                        cursor: c.cursor,
                        zIndex: 71,
                    }, onMouseDown: startResize, onClick: (e) => e.stopPropagation() }, `editor-handle-${i}`)));
            })()] }));
});
LabelInlineEditor.displayName = 'LabelInlineEditor';
